import type { QueryClient } from "@tanstack/react-query";
import i18n from "i18next";
import { ActivityKeys } from "web_myplann/Api/Activities/ActivityKeys";
import { MiscellaneousKeys } from "web_myplann/Api/Miscellaneous/miscKeys";
import { NotificationsKeys } from "web_myplann/Api/Notifications/notificatioKeys";
import { patchNotificationsIdReadV2 } from "web_myplann/Infrastructure/Api/Api";
import { handleCall } from "web_myplann/Infrastructure/Api/ApiUtils";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import type { ColorSchemeOptions } from "web_myplann/Infrastructure/Themes/ColorSchemeProvider";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { NavigationTypes } from "web_myplann/Utils/device";
import {
	NavigationDestinationType,
	type WebViewMessage,
	WebViewMessageTypes,
} from "web_myplann/Utils/WebViewModel";

declare global {
	interface Window {
		ReactNativeWebView?: {
			postMessage(message: string): void;
		};
	}
}

export const handleIncomingMessage = async (
	message: WebViewMessage,
	setMode: (variant: Partial<ColorSchemeOptions>) => void,
	queryClient: QueryClient,
	navigate: (path: string) => void,
) => {
	if (message.type === WebViewMessageTypes.SWITCH_LANGUAGE) {
		i18n.changeLanguage(message.payload.language);
	}

	if (message.type === WebViewMessageTypes.SWITCH_DARK_MODE) {
		setMode({ paletteMode: message.payload.darkMode ? "dark" : "light" });
	}

	if (message.type === WebViewMessageTypes.REFRESH_NOTIFICATIONS_REQUEST) {
		queryClient.invalidateQueries({
			queryKey: NotificationsKeys.count(),
		});
		queryClient.invalidateQueries({
			queryKey: [...ActivityKeys.all, "list"],
		});
	}

	if (message.type === WebViewMessageTypes.REFRESH_APP_INFO_REQUEST) {
		queryClient.refetchQueries({
			queryKey: MiscellaneousKeys.appColor(),
		});
	}

	if (message.type === WebViewMessageTypes.NAVIGATION_REQUEST) {
		const { url } = message.payload;

		switch (url) {
			case NavigationTypes.WebDashboard:
				navigate(Routing.Dashboard.path());
				break;
			case NavigationTypes.WebPortfolio:
				navigate(Routing.Portfolio.PortfolioList.path());
				break;
			case NavigationTypes.WebActivity:
				navigate(Routing.ActivitiesList.path());
				break;
			case NavigationTypes.GoalsScreen:
				navigate(Routing.Goals.GoalsList.path());
				break;
			case NavigationTypes.MoreScreen:
				navigate(Routing.Profile.More.path());
				break;
		}
	}

	if (message.type === WebViewMessageTypes.NAVIGATE_NOTIFICATION_REQUEST) {
		const { sourceId, type, id } = message.payload;

		const result = await handleCall(patchNotificationsIdReadV2(id));

		if (result.isSuccess) {
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.count(),
			});
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.infiniteGroupedList(true),
			});
			queryClient.invalidateQueries({
				queryKey: NotificationsKeys.infiniteGroupedList(false),
			});
		}

		if (type === NavigationDestinationType.activity) {
			navigate(AppRouting.MyPlann.ActivityDetail.path(sourceId));
		} else if (type === NavigationDestinationType.task) {
			navigate(AppRouting.MyPlann.TaskDetail.path(sourceId));
		}
	}
};
